import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ChartLine,
  CurrencyDollar,
  TrendUp,
  TrendDown,
  Clock,
  Warning,
  Receipt,
  Users,
  CreditCard,
  CaretDown,
  SpinnerGap,
  Export,
  ArrowRight
} from "phosphor-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Simple bar chart component
const SimpleBarChart = ({ data, height = 200, valueKey = "revenue", labelKey = "date" }) => {
  if (!data || data.length === 0) {
    return (
      <div className="flex items-center justify-center h-48 text-zinc-500">
        No data available for this period
      </div>
    );
  }

  const maxValue = Math.max(...data.map(d => d[valueKey]));
  const barWidth = Math.max(20, Math.min(60, (100 / data.length) - 2));

  return (
    <div className="w-full" style={{ height }}>
      <div className="flex items-end justify-between h-full gap-1 px-2">
        {data.map((item, idx) => {
          const barHeight = maxValue > 0 ? (item[valueKey] / maxValue) * 100 : 0;
          return (
            <div key={idx} className="flex flex-col items-center flex-1 min-w-0">
              <div 
                className="w-full bg-emerald-500 rounded-t transition-all hover:bg-emerald-600 cursor-pointer group relative"
                style={{ height: `${Math.max(barHeight, 2)}%`, maxWidth: `${barWidth}px` }}
                title={`${item[labelKey]}: $${item[valueKey].toLocaleString()}`}
              >
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block bg-zinc-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                  ${item[valueKey].toLocaleString()}
                </div>
              </div>
              <span className="text-[10px] text-zinc-500 mt-1 truncate w-full text-center">
                {item[labelKey].split('-').slice(-1)[0]}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Aging chart component
const AgingChart = ({ aging }) => {
  const buckets = [
    { key: "current", label: "Current", color: "bg-emerald-500" },
    { key: "1_30_days", label: "1-30 days", color: "bg-yellow-500" },
    { key: "31_60_days", label: "31-60 days", color: "bg-orange-500" },
    { key: "61_90_days", label: "61-90 days", color: "bg-red-400" },
    { key: "over_90_days", label: "90+ days", color: "bg-red-600" }
  ];

  const total = buckets.reduce((sum, b) => sum + (aging[b.key]?.amount || 0), 0);

  return (
    <div className="space-y-3">
      {buckets.map(bucket => {
        const data = aging[bucket.key] || { amount: 0, count: 0 };
        const percentage = total > 0 ? (data.amount / total) * 100 : 0;
        
        return (
          <div key={bucket.key} className="space-y-1">
            <div className="flex justify-between text-sm">
              <span className="text-zinc-600">{bucket.label}</span>
              <span className="font-medium">
                ${data.amount.toLocaleString()} 
                <span className="text-zinc-400 text-xs ml-1">({data.count})</span>
              </span>
            </div>
            <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
              <div 
                className={`h-full ${bucket.color} transition-all`}
                style={{ width: `${percentage}%` }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Payment method icon
const PaymentMethodIcon = ({ method }) => {
  const icons = {
    cash: "💵",
    check: "📝",
    credit_card: "💳",
    bank_transfer: "🏦",
    other: "💰"
  };
  return <span className="mr-2">{icons[method] || icons.other}</span>;
};

export default function BillingAnalytics() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("30d");
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [period]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/billing-analytics/dashboard?period=${period}`, {
        credentials: "include",
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch analytics");
      }
      
      const result = await response.json();
      setData(result);
    } catch (error) {
      console.error("Error fetching billing analytics:", error);
      toast.error("Failed to load billing analytics");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await fetch(`${API}/billing-analytics/export?period=${period}`, {
        credentials: "include",
      });
      
      if (!response.ok) {
        throw new Error("Export failed");
      }
      
      const exportData = await response.json();
      
      // Create downloadable JSON
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `billing-export-${period}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Export downloaded successfully");
    } catch (error) {
      toast.error("Failed to export data");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center" data-testid="billing-analytics-loading">
        <SpinnerGap className="w-8 h-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  const summary = data?.summary || {};
  const aging = data?.aging || {};

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="billing-analytics-page">
      {/* Header */}
      <div className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/settings")}
                className="text-zinc-600"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Settings
              </Button>
              <div className="h-6 w-px bg-zinc-200" />
              <h1 className="text-lg font-semibold text-zinc-900 flex items-center gap-2">
                <ChartLine className="w-5 h-5 text-emerald-600" />
                Billing Analytics
              </h1>
            </div>
            
            <div className="flex items-center gap-3">
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-36" data-testid="period-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                  <SelectItem value="90d">Last 90 days</SelectItem>
                  <SelectItem value="12m">Last 12 months</SelectItem>
                  <SelectItem value="ytd">Year to date</SelectItem>
                </SelectContent>
              </Select>
              
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                data-testid="export-btn"
              >
                <Export className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {/* Total Revenue */}
          <Card className="bg-white" data-testid="revenue-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Total Revenue</p>
                  <p className="text-2xl font-bold text-zinc-900 mt-1">
                    {formatCurrency(summary.total_revenue || 0)}
                  </p>
                  <div className="flex items-center gap-1 mt-2">
                    {summary.revenue_change >= 0 ? (
                      <TrendUp className="w-4 h-4 text-emerald-500" />
                    ) : (
                      <TrendDown className="w-4 h-4 text-red-500" />
                    )}
                    <span className={`text-sm font-medium ${summary.revenue_change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {summary.revenue_change >= 0 ? '+' : ''}{summary.revenue_change}%
                    </span>
                    <span className="text-xs text-zinc-400">vs prev period</span>
                  </div>
                </div>
                <div className="p-3 bg-emerald-50 rounded-full">
                  <CurrencyDollar className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Outstanding Balance */}
          <Card className="bg-white" data-testid="outstanding-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Outstanding</p>
                  <p className="text-2xl font-bold text-zinc-900 mt-1">
                    {formatCurrency(summary.outstanding_balance || 0)}
                  </p>
                  <p className="text-xs text-zinc-400 mt-2">
                    {summary.outstanding_count} unpaid invoice{summary.outstanding_count !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="p-3 bg-blue-50 rounded-full">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Overdue */}
          <Card className="bg-white" data-testid="overdue-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Overdue</p>
                  <p className="text-2xl font-bold text-red-600 mt-1">
                    {formatCurrency(summary.overdue_amount || 0)}
                  </p>
                  <p className="text-xs text-zinc-400 mt-2">
                    {summary.overdue_count} overdue invoice{summary.overdue_count !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="p-3 bg-red-50 rounded-full">
                  <Warning className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Collection Rate */}
          <Card className="bg-white" data-testid="collection-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-zinc-500">Collection Rate</p>
                  <p className="text-2xl font-bold text-zinc-900 mt-1">
                    {summary.collection_rate || 0}%
                  </p>
                  <p className="text-xs text-zinc-400 mt-2">
                    Avg invoice: {formatCurrency(summary.avg_invoice_value || 0)}
                  </p>
                </div>
                <div className="p-3 bg-purple-50 rounded-full">
                  <Receipt className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Revenue Trend */}
          <Card className="bg-white" data-testid="revenue-trend-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <TrendUp className="w-4 h-4 text-emerald-600" />
                Revenue Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SimpleBarChart 
                data={data?.revenue_trend || []} 
                height={200}
                valueKey="revenue"
                labelKey="date"
              />
            </CardContent>
          </Card>

          {/* Invoice Aging */}
          <Card className="bg-white" data-testid="aging-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Clock className="w-4 h-4 text-orange-600" />
                Invoice Aging
              </CardTitle>
            </CardHeader>
            <CardContent>
              <AgingChart aging={aging} />
            </CardContent>
          </Card>
        </div>

        {/* Bottom Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Payment Methods */}
          <Card className="bg-white" data-testid="payment-methods-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-blue-600" />
                Payment Methods
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data?.payment_methods?.length > 0 ? (
                <div className="space-y-3">
                  {data.payment_methods.map((pm, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center">
                        <PaymentMethodIcon method={pm.method} />
                        <span className="text-sm text-zinc-700 capitalize">
                          {pm.method?.replace('_', ' ') || 'Unknown'}
                        </span>
                      </div>
                      <div className="text-right">
                        <span className="font-medium">{formatCurrency(pm.total)}</span>
                        <span className="text-xs text-zinc-400 ml-2">({pm.count})</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-zinc-500 text-center py-4">
                  No payment data for this period
                </p>
              )}
            </CardContent>
          </Card>

          {/* Top Customers */}
          <Card className="bg-white" data-testid="top-customers-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Users className="w-4 h-4 text-emerald-600" />
                Top Customers
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data?.top_customers?.length > 0 ? (
                <div className="space-y-3">
                  {data.top_customers.map((cust, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-2 hover:bg-zinc-50 rounded cursor-pointer"
                      onClick={() => navigate(`/customers/${cust.customer_id}`)}
                    >
                      <div>
                        <p className="text-sm font-medium text-zinc-900">{cust.name}</p>
                        <p className="text-xs text-zinc-400">{cust.invoice_count} invoices</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-emerald-600">
                          {formatCurrency(cust.revenue)}
                        </span>
                        <ArrowRight className="w-4 h-4 text-zinc-300" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-zinc-500 text-center py-4">
                  No customer data for this period
                </p>
              )}
            </CardContent>
          </Card>

          {/* Recent Payments */}
          <Card className="bg-white" data-testid="recent-payments-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <CurrencyDollar className="w-4 h-4 text-emerald-600" />
                Recent Payments
              </CardTitle>
            </CardHeader>
            <CardContent>
              {data?.recent_payments?.length > 0 ? (
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {data.recent_payments.map((pmt, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between p-2 hover:bg-zinc-50 rounded cursor-pointer border-b border-zinc-100 last:border-0"
                      onClick={() => navigate(`/invoices`)}
                    >
                      <div>
                        <p className="text-sm font-medium text-zinc-900">
                          {pmt.customer_name}
                        </p>
                        <p className="text-xs text-zinc-400">
                          {pmt.invoice_number} • {formatDate(pmt.date)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-emerald-600">
                          +{formatCurrency(pmt.amount)}
                        </p>
                        <Badge variant="outline" className="text-[10px] capitalize">
                          {pmt.method?.replace('_', ' ')}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-zinc-500 text-center py-4">
                  No recent payments
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
